import type { SearchArea } from '@/lib/areaSearch';
import { dodajObszar, type WynikDodania } from '@/lib/areaSelection';
import { czySzukac, obszarZMiejsca, tekstZapytania, type PodpowiedzMiejsca } from '@/lib/bliskoDo';

/**
 * MIEJSCE Z GŁÓWNEGO POLA OBSZARU (plan §1.90, Maciek 20.10, makieta „Miejsca w wyszukiwarce”).
 *
 * Pod podpowiedziami dzielnic, osiedli i metra stoi ZAWSZE jeden wiersz „Szukaj miejsca: „…””
 * (od 3 znaków, także gdy dzielnice pasują — „Politechnika” to stacja i uczelnia). Wyszukiwanie
 * miejsc (płatne zapytanie LocationIQ przez `place-autocomplete`) leci dopiero po wybraniu tego
 * wiersza, nigdy przy pisaniu. Ten plik trzyma czyste reguły; oba widoki (pole na pulpicie i
 * nakładka na telefonie) używają ich przez `useMiejsceWPolu`.
 */

export interface StanPola {
  query: string;
  /** Fraza, dla której wybrano „Szukaj miejsca” — null, gdy pole pokazuje dzielnice i metro. */
  trybMiejsca: string | null;
}

export type AkcjaPola =
  | { typ: 'pisze'; query: string }
  | { typ: 'szukaj_miejsca' }
  | { typ: 'wroc' }
  | { typ: 'wybrano' };

export const POCZATEK_POLA: StanPola = { query: '', trybMiejsca: null };

/** Wiersz „Szukaj miejsca” — od 3 znaków po przycięciu, ten sam próg co „Blisko do…”. */
export function czyWierszMiejsca(query: string): boolean {
  return czySzukac(query);
}

export function stanPola(s: StanPola, a: AkcjaPola): StanPola {
  switch (a.typ) {
    // Każdy znak wychodzi z trybu miejsc: wyniki należały do poprzedniej frazy.
    case 'pisze':
      return { query: a.query, trybMiejsca: null };
    case 'szukaj_miejsca':
      return czyWierszMiejsca(s.query) ? { ...s, trybMiejsca: tekstZapytania(s.query) } : s;
    case 'wroc':
      return { ...s, trybMiejsca: null };
    case 'wybrano':
      return POCZATEK_POLA;
  }
}

/**
 * Co idzie do wyszukiwania miejsc. Pusty napis = nic (hook `usePodpowiedziMiejsc` nie woła
 * funkcji poniżej 3 znaków) — dlatego samo pisanie nie kosztuje ani jednego zapytania.
 */
export function frazaMiejsca(s: StanPola): string {
  return s.trybMiejsca ?? '';
}

/**
 * Wybrane miejsce jako obszar — ten sam kształt i promień, co w „Blisko do…” (`obszarZMiejsca`),
 * przez te same reguły zbiorów: nowe miejsce zastępuje poprzednie w miejscu (`dodajObszar`).
 */
export function dodajMiejsce(areas: SearchArea[], p: Pick<PodpowiedzMiejsca, 'label' | 'lat' | 'lon'>): WynikDodania {
  return dodajObszar(areas, obszarZMiejsca(p));
}

/**
 * Strzałki w liście podpowiedzi: następna DOSTĘPNA pozycja, z zawijaniem. -1 = nic nie jest
 * podświetlone (start, zanim ktoś naciśnie strzałkę). Gdy nic nie jest dostępne — -1.
 */
export function przesunAktywny(aktywny: number, dostepne: readonly boolean[], krok: 1 | -1): number {
  const n = dostepne.length;
  if (!dostepne.some(Boolean)) return -1;
  let i = aktywny < 0 ? (krok === 1 ? -1 : n) : aktywny;
  for (let proba = 0; proba < n; proba++) {
    i = (i + krok + n) % n;
    if (dostepne[i]) return i;
  }
  return -1;
}
